import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { Sparkles, FileText, Share2, Wrench, ShoppingCart, Loader2, Copy, Check, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

function StreamingResult({ content, onClose }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(content);
    setCopied(true);
    toast.success("Copiat!");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="bg-secondary/50 rounded-lg p-3 border border-border relative">
      <div className="flex justify-between items-start mb-2">
        <span className="text-xs text-primary font-bold uppercase flex items-center gap-1">
          <Sparkles className="w-3 h-3" /> Rezultat AI
        </span>
        <div className="flex gap-1">
          <button onClick={handleCopy} className="text-xs px-2 py-1 rounded bg-secondary hover:bg-primary/20 transition-colors flex items-center gap-1">
            {copied ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
            {copied ? "Copiat" : "Copiaza"}
          </button>
          <button onClick={onClose} className="text-xs px-1.5 py-1 rounded bg-secondary hover:bg-red-500/20 transition-colors">
            <X className="w-3 h-3" />
          </button>
        </div>
      </div>
      <div className="text-sm whitespace-pre-wrap leading-relaxed max-h-[400px] overflow-y-auto">{content}</div>
    </div>
  );
}

async function streamAI(url, body, onChunk, onDone) {
  const token = localStorage.getItem("token");
  const resp = await fetch(`${API}${url}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(body),
  });
  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let result = "";
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    const chunk = decoder.decode(value, { stream: true });
    const lines = chunk.split("\n").filter(l => l.startsWith("data: "));
    for (const line of lines) {
      try {
        const data = JSON.parse(line.slice(6));
        if (data.type === "text") { result += data.content; onChunk(result); }
        if (data.type === "error") { onChunk("Eroare: " + data.content); }
      } catch (e) { /* skip */ }
    }
  }
  onDone(result);
}

// ===== Generate Description Button =====
export function AIDescriptionButton({ scooterId }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState("");
  const [lang, setLang] = useState("ro");

  const generate = async () => {
    setLoading(true); setResult("");
    try {
      await streamAI("/ai/generate-description", { scooter_id: scooterId, language: lang }, setResult, () => setLoading(false));
    } catch (e) { toast.error("Eroare AI"); setLoading(false); }
  };

  return (
    <div className="space-y-2" data-testid="ai-description-tool">
      <div className="flex items-center gap-2">
        <Button size="sm" onClick={generate} disabled={loading} variant="outline" className="border-primary/50">
          {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileText className="w-4 h-4 mr-1" />}
          {loading ? "Se genereaza..." : "Genereaza descriere AI"}
        </Button>
        <Select value={lang} onValueChange={setLang}>
          <SelectTrigger className="w-[80px] h-8"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ro">RO</SelectItem>
            <SelectItem value="fr">FR</SelectItem>
            <SelectItem value="en">EN</SelectItem>
            <SelectItem value="ar">AR</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {result && <StreamingResult content={result} onClose={() => setResult("")} />}
    </div>
  );
}

// ===== Social Media Post Generator =====
export function AISocialPostButton({ scooterId }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState("");
  const [platform, setPlatform] = useState("facebook");
  const [lang, setLang] = useState("ro");

  const generate = async () => {
    setLoading(true); setResult("");
    try {
      await streamAI("/ai/social-post", { scooter_id: scooterId, platform, language: lang }, setResult, () => setLoading(false));
    } catch (e) { toast.error("Eroare AI"); setLoading(false); }
  };

  return (
    <div className="space-y-2" data-testid="ai-social-tool">
      <div className="flex items-center gap-2 flex-wrap">
        <Button size="sm" onClick={generate} disabled={loading} variant="outline" className="border-primary/50">
          {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Share2 className="w-4 h-4 mr-1" />}
          {loading ? "Se genereaza..." : "Anunt social media"}
        </Button>
        <Select value={platform} onValueChange={setPlatform}>
          <SelectTrigger className="w-[120px] h-8"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="facebook">Facebook</SelectItem>
            <SelectItem value="instagram">Instagram</SelectItem>
            <SelectItem value="olx">OLX</SelectItem>
            <SelectItem value="whatsapp">WhatsApp</SelectItem>
          </SelectContent>
        </Select>
        <Select value={lang} onValueChange={setLang}>
          <SelectTrigger className="w-[80px] h-8"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ro">RO</SelectItem>
            <SelectItem value="fr">FR</SelectItem>
            <SelectItem value="en">EN</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {result && <StreamingResult content={result} onClose={() => setResult("")} />}
    </div>
  );
}

// ===== Mechanic AI Assistant =====
export function AIMechanicButton({ scooterId, additionalContext }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState("");

  const generate = async () => {
    setLoading(true); setResult("");
    try {
      await streamAI("/ai/mechanic-assist", { scooter_id: scooterId, checklist_summary: additionalContext || "" }, setResult, () => setLoading(false));
    } catch (e) { toast.error("Eroare AI"); setLoading(false); }
  };

  return (
    <div className="space-y-2" data-testid="ai-mechanic-tool">
      <Button size="sm" onClick={generate} disabled={loading} variant="outline" className="border-orange-500/50 text-orange-400 hover:bg-orange-500/10">
        {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Wrench className="w-4 h-4 mr-1" />}
        {loading ? "Analiza AI..." : "Diagnostic AI mecanic"}
      </Button>
      {result && <StreamingResult content={result} onClose={() => setResult("")} />}
    </div>
  );
}

// ===== Parts Price Estimation =====
export function AIPartsEstimateButton({ itemLabel, scooterBrand, scooterModel, scooterYear }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState("");

  const generate = async () => {
    setLoading(true); setResult("");
    try {
      await streamAI("/ai/estimate-parts", { item_label: itemLabel, scooter_brand: scooterBrand, scooter_model: scooterModel, scooter_year: scooterYear || 0 }, setResult, () => setLoading(false));
    } catch (e) { toast.error("Eroare AI"); setLoading(false); }
  };

  return (
    <div className="space-y-2" data-testid="ai-parts-tool">
      <Button size="sm" onClick={generate} disabled={loading} variant="outline" className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10">
        {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ShoppingCart className="w-4 h-4 mr-1" />}
        {loading ? "Estimare..." : "Estimeaza pret piesa"}
      </Button>
      {result && <StreamingResult content={result} onClose={() => setResult("")} />}
    </div>
  );
}
